<template>
	<section
		ref="mastersSectionRef"
		class="m-masters"
		:class="{ 'm-masters--en': locale === 'en' }"
		:aria-busy="!contentReady"
	>
		<div class="masters-hero">
			<!-- 动画区不加骨架，始终直接渲染 -->
			<YyevaAnimation
				ref="mastersAnimationRef"
				:src="mastersVideoUrl"
				:width="496"
				:height="321"
				:use-design-viewport="true"
				:loop="false"
				:end-pause="true"
				class="masters-animation"
				@ready="handleContentReady"
				@error="handleContentReady"
				@end="handleMastersAnimationEnd"
			/>
			<span v-if="!contentReady" class="masters-skeleton__guide" aria-hidden="true"></span>
			<div v-else class="guide" role="note">
				<span class="guide__badge" aria-hidden="true">AI</span>
				<div class="guide__text">
					<p class="guide__line1">{{ t("index.masters.guide.line1") }}</p>
					<p class="guide__line2">{{ t("index.masters.guide.line2") }}</p>
				</div>
			</div>
		</div>

		<div v-if="!contentReady" class="masters-skeleton__card" aria-hidden="true">
			<span class="masters-skeleton__greeting"></span>
			<div class="masters-skeleton__tags">
				<span v-for="n in 4" :key="n" class="masters-skeleton__tag"></span>
			</div>
			<span class="masters-skeleton__input"></span>
		</div>

		<div v-else class="send-box">
			<button type="button" class="expand-btn" @click="handleSend">
				<img src="../../../images/pc/master-expand.svg" class="expand-icon" alt="" />
			</button>
			<p class="greeting">{{ t("index.masters.greeting") }}</p>

			<div class="tags">
				<button
					v-for="(item, index) in tagList"
					:key="item.key"
					type="button"
					class="tag"
					:class="{ active: selectedToolType === item.key }"
					@click="handleTagClick(item, index)"
				>
					<img :src="item.icon" class="tag-icon" alt="" />
					<span>{{ item.label }}</span>
				</button>
			</div>

			<div v-if="selectedToolType === 'bazi' && isLoggedInClient" class="panel-area">
				<BaziFilePanel :user-mess="userMess" @send="handlePanelSend" />
			</div>
			<div v-else-if="selectedToolType === 'tarot'" class="panel-area">
				<QuestionList :type="2" @send="handlePanelSend" />
			</div>
			<div v-else-if="selectedToolType === 'liuyao'" class="panel-area">
				<QuestionList :type="4" @send="handlePanelSend" />
			</div>

			<div class="input-box">
				<input
					v-model="question"
					type="text"
					class="input"
					:placeholder="placeholder"
					@focus="handleInputFocus"
					@keyup.enter="handleSend"
				/>
				<button type="button" class="send-btn" @click="handleSend">
					<img src="../../../images/pc/master-send.png" class="send-icon" alt="" />
				</button>
			</div>
		</div>
	</section>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "~/composables/useI18n";
import {
	useHomepageTracking,
	useSectionView,
} from "@variants/index/composables/useHomepageTracking";
import bazi from "../../../images/pc/bazi.png";
import tarot from "../../../images/pc/tarot.png";
import liuyao from "../../../images/pc/liuyao.png";
import chat from "../../../images/pc/chat.png";
import YyevaAnimation from "~/components/yyeva-animation.vue";
import mastersVideoUrl from "../../../pc/components/masters/masters.mp4?url";
import BaziFilePanel from "@variants/seer/master-s/m/components/BaziFilePanel.vue";
import QuestionList from "@variants/seer/master-s/m/components/QuestionList.vue";
import { t as masterT } from "@variants/seer/master-s/shared/utils/i18n";
import showMessage from "~/utils/showMessage";

const { t, locale } = useI18n();
const { track } = useHomepageTracking("mobile");
const { isLogin } = storeToRefs(useUserStore());
const openLoginPopup = inject("openLoginPopup", null);

const question = ref("");
const selectedToolType = ref("chat");
const mastersSectionRef = ref(null);
const mastersAnimationRef = ref(null);
/** 引导气泡 / 发送区是否可展示（动画区不走骨架） */
const contentReady = ref(false);
let mastersReplayTimer = null;
let contentReadyFallbackTimer = null;
const MASTERS_ANIMATION_REPLAY_DELAY = 15000;
const CONTENT_READY_FALLBACK = 4000;
const placeholder = computed(() => t("index.masters.placeholder"));

const handleContentReady = () => {
	if (contentReady.value) return;
	contentReady.value = true;
	if (contentReadyFallbackTimer) {
		clearTimeout(contentReadyFallbackTimer);
		contentReadyFallbackTimer = null;
	}
};

const clearMastersReplayTimer = () => {
	if (mastersReplayTimer) {
		clearTimeout(mastersReplayTimer);
		mastersReplayTimer = null;
	}
};

const handleMastersAnimationEnd = () => {
	clearMastersReplayTimer();
	mastersReplayTimer = setTimeout(() => {
		mastersAnimationRef.value?.start?.();
	}, MASTERS_ANIMATION_REPLAY_DELAY);
};

onMounted(() => {
	contentReadyFallbackTimer = setTimeout(handleContentReady, CONTENT_READY_FALLBACK);
});

onBeforeUnmount(() => {
	clearMastersReplayTimer();
	if (contentReadyFallbackTimer) {
		clearTimeout(contentReadyFallbackTimer);
		contentReadyFallbackTimer = null;
	}
});

const isLoggedInClient = computed(
	() => Boolean(isLogin.value) || (import.meta.client && Boolean(localStorage.getItem("token")))
);

/** 未登录不初始化 store，避免触发档案列表请求 */
const getBaziFileStore = () => {
	if (!isLoggedInClient.value) return null;
	return useBaziFileStore();
};

const userMess = computed(() => {
	const store = getBaziFileStore();
	const file = store?.baziFile;
	if (!file?.user_name) return null;
	return {
		id: Number(file.id) || 0,
		sex: Number(file.gender) || 0,
		user_name: file.user_name,
		bir_timestamp: String(file.bir_timestamp || ""),
	};
});

const hasBaziFile = computed(() => Boolean(userMess.value?.user_name));

const ensureBaziProfile = async () => {
	const store = getBaziFileStore();
	if (!store) return;
	// 每次进入八字面板都拉最新档案（刷新后 isGetFileList 不再持久化）
	await store.getbaziFileList?.().catch(() => {});
};

watch(
	[selectedToolType, isLoggedInClient],
	([key, loggedIn]) => {
		if (key === "bazi" && loggedIn) ensureBaziProfile();
	},
	{ immediate: true }
);

const tagList = computed(() => [
	{ key: "bazi", modelType: 1, icon: bazi, label: t("index.masters.tags.bazi") },
	{ key: "tarot", modelType: 2, icon: tarot, label: t("index.masters.tags.tarot") },
	{ key: "liuyao", modelType: 3, icon: liuyao, label: t("index.masters.tags.liuyao") },
	{ key: "chat", modelType: 4, icon: chat, label: t("index.masters.tags.chat") },
]);

useSectionView(mastersSectionRef, {
	track,
	sectionId: "masters_chat",
});

const isClientLoggedIn = () =>
	import.meta.client && Boolean(localStorage.getItem("token") || isLogin.value);

/** 登录成功后裸进 MasterS，不带 question / model */
const goToMasterSBare = () => {
	navigateTo({ path: "/seer/master-s" });
};

const requireLogin = source => {
	openLoginPopup?.(goToMasterSBare, null, true, source);
};

const handleTagClick = (item, index) => {
	selectedToolType.value = item.key;
	track("masters_tool_tab_click", {
		tool_type: item.key,
		element_text: item.label,
		position: index + 1,
	});
	if (!isClientLoggedIn()) {
		requireLogin("masters_home_tag");
	}
};

const handleInputFocus = event => {
	track("masters_input_focus", {
		...(selectedToolType.value ? { selected_tool_type: selectedToolType.value } : {}),
	});
};

const resolveHomeModelType = () => {
	const selected = tagList.value.find(item => item.key === selectedToolType.value);
	return selected?.modelType || 4;
};

/** 已登录：带当前 model，有内容则带 question */
const goToMasterS = () => {
	const trimmed = question.value.trim();
	navigateTo({
		path: "/seer/master-s",
		query: {
			model: String(resolveHomeModelType()),
			...(trimmed ? { question: trimmed } : {}),
		},
	});
};

const handleSend = async () => {
	const trimmed = question.value.trim();

	if (!isClientLoggedIn()) {
		track("masters_question_submit", {
			...(selectedToolType.value ? { selected_tool_type: selectedToolType.value } : {}),
			question_length: trimmed.length,
			result: "fail",
		});
		requireLogin("masters_home_send");
		return;
	}

	// 八字无档案不能发送问题（空发送仍可进 MasterS）
	if (selectedToolType.value === "bazi" && trimmed) {
		await ensureBaziProfile();
		if (!hasBaziFile.value) {
			track("masters_question_submit", {
				selected_tool_type: "bazi",
				question_length: trimmed.length,
				result: "fail",
			});
			showMessage(masterT("请添加档案"), "warning");
			return;
		}
	}

	track("masters_question_submit", {
		...(selectedToolType.value ? { selected_tool_type: selectedToolType.value } : {}),
		question_length: trimmed.length,
		result: "success",
	});
	goToMasterS();
};

/** 点击问题只填入输入框，不跳转；发送由发送按钮触发 */
const handlePanelSend = text => {
	question.value = String(text || "").trim();
};
</script>

<style lang="scss" scoped>
@keyframes masters-guide-bounce {
	0%,
	100% {
		transform: translateY(0);
	}
	20% {
		transform: translateY(-10px);
	}
	40% {
		transform: translateY(0);
	}
	55% {
		transform: translateY(-6px);
	}
	70% {
		transform: translateY(0);
	}
	82% {
		transform: translateY(-3px);
	}
}

@keyframes masters-skeleton-shimmer {
	to {
		background-position: -100% 0;
	}
}

.m-masters {
	position: relative;
	padding: 0 24px 40px;
	margin-top: 100px;

	.masters-skeleton__guide,
	.masters-skeleton__greeting,
	.masters-skeleton__tag,
	.masters-skeleton__input {
		display: block;
		background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
		background-size: 300% 100%;
		animation: masters-skeleton-shimmer 1.35s ease-in-out infinite;
	}

	.masters-skeleton__guide {
		position: absolute;
		top: 150px;
		right: 30px;
		width: 220px;
		height: 72px;
		border-radius: 16px;
	}

	.masters-skeleton__card {
		position: relative;
		margin-top: -20px;
		padding: 28px 24px 24px;
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0 4px 24px rgba(181, 125, 0, 0.16);
		border: 1px solid #ffe19d;
		box-sizing: border-box;
	}

	.masters-skeleton__greeting {
		width: 70%;
		height: 68px;
		margin-bottom: 20px;
		border-radius: 16px;
	}

	.masters-skeleton__tags {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-bottom: 20px;
	}

	.masters-skeleton__tag {
		width: 160px;
		height: 52px;
		border-radius: 999px;
	}

	.masters-skeleton__input {
		width: 100%;
		height: 72px;
		border-radius: 999px;
	}

	.masters-hero {
		position: relative;
		background: url("../../../images/m/mask.png") no-repeat center top/ 100% auto;
		width: 750px;
		height: 277px;

		.masters-animation {
			position: absolute;
			left: 50%;
			bottom: 20px;
			transform: translateX(-50%);
			pointer-events: none;
		}

		.guide {
			position: absolute;
			top: 150px;
			right: 30px;
			display: flex;
			align-items: center;
			gap: 6px;
			box-sizing: border-box;
			width: 220px;
			height: 72px;
			padding: 0 12px;
			background: url("../../../images/pc/bubble.png") no-repeat center / 100% 100%;
			animation: masters-guide-bounce 1.6s ease-out both;

			&__badge {
				flex-shrink: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				width: 36px;
				height: 36px;
				border-radius: 8px;
				background: linear-gradient(180deg, #ffb84d 0%, #f5a623 100%);
				font-size: 26px;
				line-height: 1;
				color: #ffffff;
				margin: 0 4px;
			}

			&__text {
				flex: 1;
				min-width: 0;
				display: flex;
				flex-direction: column;
				justify-content: center;
				gap: 2px;
			}

			&__line1,
			&__line2 {
				margin: 0;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			&__line1 {
				font-size: 18px;
				line-height: 26px;
				color: #333333;
			}

			&__line2 {
				font-size: 18px;
				line-height: 24px;
				color: #333333;
			}
		}
	}

	.send-box {
		position: relative;
		margin-top: -20px;
		padding: 28px 24px 24px;
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0 4px 24px rgba(181, 125, 0, 0.16);
		border: 1px solid #ffe19d;

		.expand-btn {
			position: absolute;
			top: 29px;
			right: 22px;
			z-index: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 30px;
			height: 30px;
			padding: 0;
			border: none;
			background: transparent;
			cursor: pointer;

			&:hover {
				opacity: 0.85;
			}

			.expand-icon {
				display: block;
				width: 30px;
				height: 30px;
			}
		}

		.greeting {
			display: inline-block;
			margin: 0 0 20px;
			padding: 16px 20px;
			background: #f5f5f5;
			border-radius: 16px;
			font-size: 26px;
			line-height: 36px;
			color: #333333;
			max-width: 85%;
		}

		.panel-area {
			margin: 0 0 20px;
		}

		.tags {
			display: flex;
			flex-wrap: wrap;
			gap: 12px;
			margin-bottom: 20px;

			.tag {
				display: inline-flex;
				justify-content: center;
				align-items: center;
				gap: 8px;
				padding: 10px 20px;
				border: 1px solid #fff4dd;
				border-radius: 999px;
				background: linear-gradient(to bottom, #ffedac 0%, #fffefc 100%);
				cursor: pointer;
				font-size: 26px;
				line-height: 32px;
				color: #333333;

				&.active {
					background: #333333;
					border-color: #333333;
					color: #ffffff;

					.tag-icon {
						filter: brightness(0) invert(1);
					}
				}

				.tag-icon {
					width: 20px;
					height: 20px;
					flex-shrink: 0;
				}
			}
		}

		.input-box {
			display: flex;
			align-items: center;
			min-height: 72px;
			padding: 10px 10px 10px 24px;
			background: #f5f5f5;
			border-radius: 999px;

			.input {
				flex: 1;
				min-width: 0;
				border: none;
				background: transparent;
				outline: none;
				font-size: 26px;
				line-height: 36px;
				color: #333333;

				&::placeholder {
					color: #999999;
				}
			}

			.send-btn {
				flex-shrink: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				border: none;
				padding: 0;
				background: transparent;
				cursor: pointer;

				.send-icon {
					width: 52px;
					height: 52px;
				}
			}
		}
	}

	&--en {
		.guide__line1 {
			font-size: 17px;
			line-height: 22px;
		}

		.guide__line2 {
			font-size: 17px;
			line-height: 20px;
		}

		.greeting {
			font-size: 22px;
			line-height: 30px;
		}

		.tag {
			font-size: 20px;
		}
	}
}
</style>
